import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, Lock, Pencil, Settings as SettingsIcon, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { FButton } from "@/components/forkd/FButton";
import { useLogSheet } from "@/contexts/LogSheetContext";
import { categoryEmoji, rarityColour, type Rarity } from "@/lib/achievementEngine";
import { VisitDetailSheet } from "@/components/forkd/VisitDetailSheet";
import { BadgeShowcaseEditor } from "@/components/forkd/BadgeShowcaseEditor";
import { FollowingSheet } from "@/components/forkd/FollowingSheet";

type VisitTile = {
  id: string;
  photo_urls: string[] | null;
  restaurant: {
    id: string;
    name: string;
    photo_url: string | null;
    restaurant_cuisine_types: { cuisine_types: { emoji: string | null } | null }[] | null;
  } | null;
};

type Profile = {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  total_visits: number;
  total_badges: number;
};

type Badge = {
  user_achievement_id: string;
  earned_at: string;
  id: string;
  name: string;
  description: string;
  category: string;
  rarity: Rarity;
  colour_hex: string;
};

export default function Profile() {
  const { username } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const { openSheet } = useLogSheet();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [followingCount, setFollowingCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState<VisitTile[]>([]);
  const [showcased, setShowcased] = useState<Badge[]>([]);
  const [openVisitId, setOpenVisitId] = useState<string | null>(null);
  const [editingShowcase, setEditingShowcase] = useState(false);
  const [activeBadge, setActiveBadge] = useState<Badge | null>(null);
  const [showAvatarNudge, setShowAvatarNudge] = useState(false);
  const [followingOpen, setFollowingOpen] = useState(false);

  const isOwn = !username || (profile && user?.id === profile.id);

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url, bio, total_visits, total_badges");
    const { data: row } = username
      ? await query.eq("username", username).maybeSingle()
      : await query.eq("id", user?.id ?? "").maybeSingle();
    setProfile((row as Profile) ?? null);

    if (row) {
      const pid = (row as Profile).id;
      const { count } = await supabase
        .from("follows")
        .select("*", { count: "exact", head: true })
        .eq("follower_id", pid);
      setFollowingCount(count ?? 0);

      if (user && pid !== user.id) {
        const { data: f } = await supabase
          .from("follows")
          .select("id")
          .eq("follower_id", user.id)
          .eq("following_id", pid)
          .maybeSingle();
        setIsFollowing(!!f);
      }

      const [{ data: visitRows }, { data: badgeRows }] = await Promise.all([
        supabase
          .from("visits")
          .select(
            "id, photo_urls, restaurant:restaurants(id, name, photo_url, restaurant_cuisine_types(cuisine_types(emoji)))",
          )
          .eq("user_id", pid)
          .order("confirmed_at", { ascending: false })
          .limit(60),
        supabase
          .from("user_achievements")
          .select(
            "id, earned_at, is_showcased, achievement:achievements(id, name, description, category, rarity, colour_hex)",
          )
          .eq("user_id", pid)
          .order("is_showcased", { ascending: false })
          .order("earned_at", { ascending: false })
          .limit(5),
      ]);
      setVisits((visitRows ?? []) as unknown as VisitTile[]);
      setShowcased(
        ((badgeRows ?? []) as any[])
          .filter((r) => r.achievement)
          .map((r) => ({
            user_achievement_id: r.id,
            earned_at: r.earned_at,
            ...r.achievement,
          })),
      );
    }
    setLoading(false);
  }, [username, user?.id, user]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isOwn || !profile) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem("forkd_avatar_nudge_dismissed") === "1";
    } catch {}
    setShowAvatarNudge(!dismissed && !profile.avatar_url);
  }, [isOwn, profile]);

  const dismissAvatarNudge = () => {
    setShowAvatarNudge(false);
    try {
      localStorage.setItem("forkd_avatar_nudge_dismissed", "1");
    } catch {}
  };

  const toggleFollow = async () => {
    if (!user || !profile) return;
    if (isFollowing) {
      await supabase
        .from("follows")
        .delete()
        .eq("follower_id", user.id)
        .eq("following_id", profile.id);
      setIsFollowing(false);
    } else {
      const { error } = await supabase
        .from("follows")
        .insert({ follower_id: user.id, following_id: profile.id });
      if (error) {
        toast.error(error.message);
        return;
      }
      setIsFollowing(true);
    }
  };

  if (loading) {
    return (
      <div className="px-5 pt-6">
        <div className="h-6 w-24 mx-auto bg-[var(--colour-surface-raised)] rounded animate-pulse mb-8" />
        <div className="w-24 h-24 mx-auto rounded-[var(--radius-full)] bg-[var(--colour-surface-raised)] animate-pulse" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-5 text-center">
        <p className="text-[var(--colour-text-secondary)]">Profile not found.</p>
      </div>
    );
  }

  const emptyBadgeSlots = Math.max(0, 5 - showcased.length);

  return (
    <div>
      {/* Header */}
      <header className="flex items-center justify-between px-4 h-14">
        {!isOwn ? (
          <button
            onClick={() => nav(-1)}
            aria-label="Back"
            className="w-11 h-11 flex items-center justify-center rounded-[var(--radius-full)]"
          >
            <ArrowLeft size={22} />
          </button>
        ) : (
          <span className="w-11" />
        )}
        <h1 className="text-[16px] font-semibold">@{profile.username}</h1>
        {isOwn ? (
          <Link
            to="/settings"
            aria-label="Settings"
            className="w-11 h-11 flex items-center justify-center rounded-[var(--radius-full)]"
          >
            <SettingsIcon size={22} />
          </Link>
        ) : (
          <span className="w-11" />
        )}
      </header>

      {isOwn && showAvatarNudge && (
        <Link
          to="/settings"
          className="mx-4 mt-1 mb-2 flex items-center gap-3 px-3.5 py-3 rounded-[var(--radius-md)] border"
          style={{
            borderColor: "var(--colour-border)",
            backgroundColor: "var(--colour-surface)",
          }}
        >
          <Camera size={18} className="text-[var(--colour-accent)] shrink-0" />
          <span className="flex-1 text-[14px] font-medium">Add a photo to your profile</span>
          <button
            aria-label="Dismiss"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dismissAvatarNudge();
            }}
            className="w-9 h-9 -mr-1 flex items-center justify-center text-[var(--colour-text-secondary)]"
          >
            <X size={18} />
          </button>
        </Link>
      )}


      {/* Avatar section */}
      <section className="flex flex-col items-center px-5 pt-2 pb-6 text-center">
        <div
          className="rounded-[var(--radius-full)] p-[3px]"
          style={{ backgroundColor: "var(--colour-accent)" }}
        >
          {profile.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt={profile.display_name}
              className="w-24 h-24 rounded-[var(--radius-full)] object-cover bg-[var(--colour-surface)]"
            />
          ) : (
            <div className="w-24 h-24 rounded-[var(--radius-full)] bg-[var(--colour-surface-raised)] flex items-center justify-center text-[32px] font-semibold text-[var(--colour-text-secondary)]">
              {profile.display_name.slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <h2 className="mt-3 text-[22px] font-bold">{profile.display_name}</h2>
        <p className="text-[14px] text-[var(--colour-text-secondary)]">
          @{profile.username}
        </p>
        {profile.bio && (
          <p className="mt-2 text-[15px] max-w-[320px]">{profile.bio}</p>
        )}

        <div className="mt-4">
          {isOwn ? (
            <Link to="/settings">
              <FButton variant="outline" className="px-6">
                Edit profile
              </FButton>
            </Link>
          ) : (
            <FButton
              variant={isFollowing ? "outline" : "primary"}
              onClick={toggleFollow}
              className="px-8"
            >
              {isFollowing ? "Following" : "Follow"}
            </FButton>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="px-4">
        <div className="grid grid-cols-3 gap-2.5">
          <Stat value={profile.total_visits} label="Visits" />
          <Stat value={profile.total_badges} label="Badges" />
          <Stat
            value={followingCount}
            label="Following"
            onClick={() => setFollowingOpen(true)}
          />
        </div>
      </section>

      {/* Badges */}
      <section className="px-4 mt-7">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[16px] font-semibold">Badges</h3>
          {isOwn && profile.total_badges > 0 && (
            <button
              onClick={() => setEditingShowcase(true)}
              className="inline-flex items-center gap-1 text-[13px] text-[var(--colour-text-secondary)] min-h-[36px] px-1"
            >
              <Pencil size={14} />
              Edit
            </button>
          )}
        </div>
        <div className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4">
          {showcased.map((b) => (
            <button
              key={b.user_achievement_id}
              onClick={() => setActiveBadge(b)}
              className="shrink-0 w-16 h-16 rounded-[var(--radius-full)] flex items-center justify-center text-[28px]"
              style={{
                backgroundColor: b.colour_hex + "22",
                border: `2px solid ${b.colour_hex}`,
              }}
              aria-label={b.name}
            >
              {categoryEmoji(b.category)}
            </button>
          ))}
          {Array.from({ length: emptyBadgeSlots }).map((_, i) => (
            <div
              key={`lock-${i}`}
              className="shrink-0 w-16 h-16 rounded-[var(--radius-full)] border-2 border-dashed flex items-center justify-center"
              style={{ borderColor: "var(--colour-border)" }}
            >
              <Lock size={20} className="text-[var(--colour-border)]" />
            </div>
          ))}
        </div>
      </section>

      {/* Visits */}
      <section className="px-4 mt-7 mb-8">
        <h3 className="text-[16px] font-semibold mb-3">Visits</h3>
        {visits.length === 0 ? (
          <div className="bg-[var(--colour-surface)] rounded-[var(--radius-md)] border border-[var(--colour-border)] p-8 flex flex-col items-center text-center">
            <div className="text-[56px] mb-2" aria-hidden>🗺️</div>
            <h4 className="text-[18px] font-bold">No visits yet</h4>
            <p className="text-[14px] text-[var(--colour-text-secondary)] mt-1 mb-4">
              Tap + to log your first meal.
            </p>
            {isOwn && <FButton onClick={() => openSheet()}>Log a meal</FButton>}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1">
            {visits.map((v) => {
              const photo = v.restaurant?.photo_url ?? null;
              const emoji =
                v.restaurant?.restaurant_cuisine_types?.[0]?.cuisine_types?.emoji ??
                "🍽️";
              const name = v.restaurant?.name ?? "Visit";
              return (
                <button
                  key={v.id}
                  onClick={() => setOpenVisitId(v.id)}
                  className="relative aspect-square overflow-hidden bg-[var(--colour-surface-raised)] flex items-center justify-center text-center"
                  style={{ border: "1px solid #E5E2DC", borderRadius: "16px" }}
                >
                  {photo ? (
                    <>
                      <img
                        src={photo}
                        alt={name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div
                        aria-hidden
                        className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
                        }}
                      />
                      <span className="absolute inset-x-1 bottom-1 text-[10px] font-medium text-white line-clamp-2 px-1">
                        {name}
                      </span>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center px-1 gap-1">
                      <span className="text-[24px] leading-none">{emoji}</span>
                      <span className="text-[10px] font-medium text-[var(--colour-text-secondary)] line-clamp-2">
                        {name}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <VisitDetailSheet
        visitId={openVisitId}
        open={openVisitId !== null}
        onClose={() => setOpenVisitId(null)}
      />

      {isOwn && (
        <BadgeShowcaseEditor
          userId={profile.id}
          open={editingShowcase}
          onClose={() => setEditingShowcase(false)}
          onSaved={load}
        />
      )}

      {activeBadge && (
        <BadgeDetail badge={activeBadge} onClose={() => setActiveBadge(null)} />
      )}

      <FollowingSheet
        userId={profile.id}
        open={followingOpen}
        onClose={() => setFollowingOpen(false)}
      />
    </div>
  );
}

function BadgeDetail({ badge, onClose }: { badge: Badge; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <div
        className="relative w-full max-w-sm p-6 rounded-[var(--radius-lg)] text-center"
        style={{
          backgroundColor: "var(--colour-surface)",
          borderTop: `4px solid ${badge.colour_hex}`,
        }}
      >
        <div
          className="mx-auto w-20 h-20 rounded-[var(--radius-full)] flex items-center justify-center text-[40px]"
          style={{
            backgroundColor: badge.colour_hex + "22",
            border: `2px solid ${badge.colour_hex}`,
          }}
        >
          {categoryEmoji(badge.category)}
        </div>
        <div
          className="mt-3 text-[10px] font-bold uppercase tracking-wider"
          style={{ color: rarityColour(badge.rarity) }}
        >
          {badge.rarity}
        </div>
        <h3 className="mt-1 text-[20px] font-bold">{badge.name}</h3>
        <p className="mt-1 text-[14px] text-[var(--colour-text-secondary)]">
          {badge.description}
        </p>
        <p className="mt-3 text-[12px] text-[var(--colour-text-secondary)]">
          Earned {new Date(badge.earned_at).toLocaleDateString()}
        </p>
        <div className="mt-5">
          <FButton fullWidth onClick={onClose}>
            Close
          </FButton>
        </div>
      </div>
    </div>
  );
}

function Stat({
  value,
  label,
  onClick,
}: {
  value: number;
  label: string;
  onClick?: () => void;
}) {
  const className =
    "bg-[var(--colour-surface)] py-4 px-2 text-center w-full";
  const style: React.CSSProperties = {
    border: "1px solid #E5E2DC",
    borderRadius: "20px",
    boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
  };
  const inner = (
    <>
      <div
        className="text-[22px] font-bold leading-none"
        style={{ color: "var(--colour-accent)" }}
      >
        {value}
      </div>
      <div className="mt-1 text-[12px] text-[var(--colour-text-secondary)]">
        {label}
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={className + " active:opacity-80 transition-opacity"}
        style={style}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={className} style={style}>
      {inner}
    </div>
  );
}
